import { useEffect } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  HStack,
  Skeleton,
  Text,
  VStack,
  chakra,
} from '@hivewatch/dark'

import {
  getRuleHref,
  getTranslatedActionText,
} from '@/features/incident-rules-table'
import { useRulesScopedToDeviceQuery } from '@/graphql/generated/hooks'
import { BUTTON_PRESS, TAB_PRESS, trackEvent } from '@/utils/analytics'

interface IProps {
  deviceId: string
}

/**
 * Automations tab of the dark device drawer: lists the active rules whose
 * device scope explicitly includes this device, as name + action. Each rule
 * name links to the rule's detail page in a new tab and tracks the click with
 * the device and rule ids. A warning above the list calls out that scope,
 * since rules can also reach a device indirectly.
 */
export const DarkDeviceAutomationsTab = ({ deviceId }: IProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const prefix = colorScheme === 'light' ? 'light.' : ''

  useEffect(() => {
    trackEvent(`${TAB_PRESS} Device Drawer - Automations`, {
      device_id: deviceId,
    })
  }, [deviceId])

  const {
    data,
    loading: isLoading,
    error,
  } = useRulesScopedToDeviceQuery({
    variables: { deviceIds: [deviceId], activeOnly: true },
    skip: !deviceId,
  })

  const rules =
    data?.rules?.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) ?? []

  // Tracking only: the anchor's default navigation (new tab) is left intact.
  const handleRuleClick = (ruleId: string) => {
    trackEvent(`${BUTTON_PRESS} Device Drawer - Automations > Rule`, {
      device_id: deviceId,
      rule_id: ruleId,
    })
  }

  return (
    <Box data-testid='device-automations-tab' px='24px' py='16px'>
      <Box mb='16px'>
        <Alert
          colorScheme={colorScheme}
          title={t('devices:drawer_automations_scope_warning')}
          variant='warning'
        />
      </Box>
      {isLoading ? (
        <VStack align='stretch' gap='8px'>
          <Skeleton borderRadius='8px' h='24px' />
          <Skeleton borderRadius='8px' h='24px' />
          <Skeleton borderRadius='8px' h='24px' />
        </VStack>
      ) : error ? (
        <Box color={`${prefix}status.error-text`} py='16px' textAlign='center'>
          {t('devices:drawer_automations_error')}
        </Box>
      ) : rules.length === 0 ? (
        <Box color={`${prefix}text.secondary`} py='16px' textAlign='center'>
          {t('devices:drawer_automations_empty')}
        </Box>
      ) : (
        <VStack align='stretch' gap='12px'>
          {rules.map((rule) => (
            <HStack
              alignItems='center'
              data-testid='device-automation-row'
              gap='12px'
              justifyContent='space-between'
              key={rule.id}
            >
              <chakra.a
                _hover={{ textDecoration: 'underline' }}
                color={`${prefix}text.primary`}
                data-testid='device-automation-link'
                href={getRuleHref(rule.id, rule.isV2)}
                onClick={() => handleRuleClick(rule.id)}
                overflow='hidden'
                rel='noopener noreferrer'
                target='_blank'
                textOverflow='ellipsis'
                whiteSpace='nowrap'
              >
                {rule.name}
              </chakra.a>
              <Text color={`${prefix}text.secondary`} flexShrink={0}>
                {getTranslatedActionText(t, rule.action)}
              </Text>
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  )
}
